import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip } from 'components';
import { authService } from '../../api/services/auth';

import './Navbar.scss';

export const Navbar = ({ title = 'GIGACORE', customActions = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isMobile, setIsMobile] = useState(false);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [isExpanded, setIsExpanded] = useState(false);
  const isAdmin = user?.role === 'admin';
  const hideTimeoutRef = useRef(null);

  // Check if we're on mobile and set initial navbar state
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      // Set initial expanded state: expanded on desktop when not authenticated, collapsed on mobile
      if (!mobile && !isAuthenticated) {
        setIsExpanded(true);
      } else if (mobile) {
        setIsExpanded(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, [isAuthenticated]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  const publicNavigationItems = [];

  const unauthenticatedNavigationItems = [
    {
      id: 'login',
      label: 'Login',
      action: 'login',
      className: 'login-button',
    },
    {
      id: 'signup',
      label: 'Sign Up',
      action: 'signup',
      className: 'signup-button',
    },
  ];

  const authenticatedNavigationItems = [
    {
      id: 'profile',
      label: 'Profile',
      route: '/profile',
      className: 'profile-button',
    },
    {
      id: 'prompts',
      label: 'Prompts',
      route: '/prompts',
      className: 'prompts-button',
    },
    {
      id: 'build',
      label: 'Build',
      route: '/build',
      className: 'build-button',
      disabled: !isAdmin,
    },
    {
      id: 'contracts',
      label: 'Contracts',
      route: '/contracts',
      className: 'contracts-button',
      disabled: !isAdmin,
    },
    // Only show admin link for admin users
    ...(isAdmin
      ? [
          {
            id: 'admin',
            label: 'Admin',
            route: '/admin',
            className: 'admin-button',
          },
        ]
      : []),
    // Logout button for authenticated users
    {
      id: 'logout',
      label: 'Logout',
      action: 'logout', // Special action instead of route
      className: 'logout-button',
    },
  ];

  // Combine navigation items based on authentication status
  const navigationItems = [
    ...publicNavigationItems,
    ...(isAuthenticated
      ? authenticatedNavigationItems
      : unauthenticatedNavigationItems),
  ];

  const handleTitleClick = () => {
    navigate('/');
  };

  const handleNavClick = (route, disabled, action) => {
    if (disabled) {
      return; // Don't navigate if disabled
    }

    // Handle special actions
    if (action === 'logout') {
      handleLogoutClick();
      return;
    }

    if (action === 'login') {
      handleLoginClick();
      return;
    }

    if (action === 'signup') {
      handleSignupClick();
      return;
    }

    navigate(route);
    // Hide mobile menu after navigation
    if (isMobile) {
      setIsExpanded(false);
    }
  };

  const handleLoginClick = () => {
    navigate('/login');
    // Hide mobile menu after navigation
    if (isMobile) {
      setIsExpanded(false);
    }
  };

  const handleSignupClick = () => {
    // Navigate to landing page with signup parameter to trigger contact modal
    navigate('/?signup=core');
    // Hide mobile menu after action
    if (isMobile) {
      setIsExpanded(false);
    }
  };

  const handleLogoutClick = async () => {
    try {
      await authService.logout(dispatch);
      navigate('/');
      // After logout, expand navbar only on desktop (not mobile)
      if (!isMobile) {
        setIsExpanded(true);
      }
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, redirect to home (auth state is already cleared)
      navigate('/');
      // After logout, expand navbar only on desktop (not mobile)
      if (!isMobile) {
        setIsExpanded(true);
      }
    }
  };

  const handleMobileToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleMouseEnter = () => {
    if (!isMobile) {
      // Clear any existing timeout when mouse enters
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
      setIsExpanded(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile && isAuthenticated) {
      // Only hide navbar on mouse leave if user is authenticated
      // When not authenticated on desktop, navbar should stay expanded
      hideTimeoutRef.current = setTimeout(() => {
        setIsExpanded(false);
        hideTimeoutRef.current = null;
      }, 300); // 300ms delay - adjust this value as needed
    }
  };

  return (
    <nav
      className={`glass-navbar ${isExpanded ? 'expanded' : 'collapsed'}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className='glass-navbar-content'>
        <h1
          className='glass-navbar-title'
          onClick={handleTitleClick}
          role='button'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleTitleClick();
            }
          }}
        >
          {title}
        </h1>

        {/* Mobile toggle button */}
        {isMobile && (
          <button
            className='mobile-toggle'
            onClick={handleMobileToggle}
            aria-label='Toggle navigation menu'
          >
            <div className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        )}

        <div
          className={`glass-navbar-expandable ${
            isExpanded ? 'visible' : 'hidden'
          }`}
        >
          {navigationItems.map((item) => {
            const button = (
              <button
                key={item.id}
                className={`glass-nav-button ${item.className} ${
                  item.disabled ? 'disabled' : ''
                }`}
                onClick={() =>
                  handleNavClick(item.route, item.disabled, item.action)
                }
                aria-label={`Go to ${item.label}`}
                disabled={item.disabled}
              >
                {item.label}
              </button>
            );

            // Wrap disabled buttons with tooltip
            if (item.disabled) {
              return (
                <Tooltip key={item.id} content='Coming Soon' position='bottom'>
                  <button
                    className={`glass-nav-button ${item.className} disabled-with-tooltip`}
                    onClick={() =>
                      handleNavClick(item.route, item.disabled, item.action)
                    }
                    aria-label={`Go to ${item.label}`}
                  >
                    {item.label}
                  </button>
                </Tooltip>
              );
            }

            return button;
          })}

          {customActions && (
            <div className='glass-navbar-actions'>{customActions}</div>
          )}
        </div>
      </div>
    </nav>
  );
};
